import Vue from 'vue'
import Vuex from 'vuex'
import { login as loginApi, logout as logoutApi, getCurrentAdmin } from '@/api/modules/auth'
import menu from './modules/menu'

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    menu
  },
  state: {
    user: null,
    token: localStorage.getItem('token') || '',
    tokenHeader: 'Authorization',
    tokenHead: 'Bearer ',
    isMobile: false,
    sidebarCollapsed: false
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_TOKEN(state, tokenData) {
      if (typeof tokenData === 'string') {
        // 兼容旧的字符串token格式
        state.token = tokenData
        localStorage.setItem('token', tokenData)
      } else {
        // 新的token对象格式
        state.token = tokenData.token
        state.tokenHeader = tokenData.tokenHeader || 'Authorization'
        state.tokenHead = tokenData.tokenHead || 'Bearer '
        localStorage.setItem('token', tokenData.token)
        localStorage.setItem('tokenHeader', tokenData.tokenHeader || 'Authorization')
        localStorage.setItem('tokenHead', tokenData.tokenHead || 'Bearer ')
      }
    },
    CLEAR_AUTH(state) {
      state.user = null
      state.token = ''
      state.tokenHeader = 'Authorization'
      state.tokenHead = 'Bearer '
      localStorage.removeItem('token')
      localStorage.removeItem('tokenHeader')
      localStorage.removeItem('tokenHead')
    },
    SET_MOBILE(state, isMobile) {
      state.isMobile = isMobile
    },
    TOGGLE_SIDEBAR(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed
    }
  },
  actions: {
    /**
     * 用户登录
     * @param {Object} context - Vuex context
     * @param {Object} loginData - 登录数据
     * @param {string} loginData.username - 用户名
     * @param {string} loginData.password - 密码
     * @param {string} loginData.captchaKey - 验证码Key
     * @param {string} loginData.captchaCode - 验证码
     * @returns {Promise} 登录结果
     */
    async login({ commit, dispatch }, { username, password, captchaKey, captchaCode }) {
      try {
        // 调用登录API
        const tokenData = await loginApi({ username, password, captchaKey, captchaCode })
        
        // 保存token信息
        commit('SET_TOKEN', tokenData)
        
        // 获取用户信息
        const userData = await getCurrentAdmin()
        commit('SET_USER', userData)
        
        // 清除旧的菜单数据，确保重新登录时获取新用户的菜单权限
        dispatch('menu/clearMenuData', null, { root: true })
        
        return { token: tokenData.token, user: userData }
      } catch (error) {
        // 清除可能存在的错误状态
        commit('CLEAR_AUTH')
        throw error
      }
    },
    
    /**
     * 用户退出登录
     * @param {Object} context - Vuex context
     * @param {Object} router - Vue Router实例
     * @returns {Promise} 退出结果
     */
    async logout({ commit, dispatch }, router) {
      try {
        // 调用退出API
        await logoutApi()
      } catch (error) {
        // 即使API调用失败，也要清除本地状态
        console.warn('退出登录API调用失败:', error)
      } finally {
        // 清除菜单数据和动态路由
        if (router) {
          dispatch('menu/clearMenuData', router)
        }
        // 清除本地认证状态
        commit('CLEAR_AUTH')
      }
    },
    
    /**
     * 获取当前用户信息
     * @param {Object} context - Vuex context
     * @returns {Promise} 用户信息
     */
    async getCurrentUser({ commit, state }) {
      if (!state.token) {
        throw new Error('未登录')
      }
      
      try {
        const userData = await getCurrentAdmin()
        commit('SET_USER', userData)
        return userData
      } catch (error) {
        // 如果获取用户信息失败，可能是token过期
        if (error.message.includes('401') || error.message.includes('登录')) {
          commit('CLEAR_AUTH')
        }
        throw error
      }
    },
    
    /**
     * 检查移动端状态
     * @param {Object} context - Vuex context
     */
    checkMobile({ commit }) {
      const isMobile = window.innerWidth <= 768
      commit('SET_MOBILE', isMobile)
    }
  },
  getters: {
    isAuthenticated: state => !!state.token,
    currentUser: state => state.user,
    token: state => state.token,
    tokenHeader: state => state.tokenHeader,
    tokenHead: state => state.tokenHead,
    userPermissions: state => (state.user && state.user.permissionList) || []
  }
})